import React, { useState, useRef, useEffect } from 'react';

const PURPOSE_OPTIONS = [
  { key: 'purpose_education',   label: 'Education (Line 11)' },
  { key: 'purpose_health',      label: 'Health and Hospital (Line 12)' },
  { key: 'purpose_transport',   label: 'Transportation (Line 13)' },
  { key: 'purpose_safety',      label: 'Public Safety (Line 14)' },
  { key: 'purpose_environment', label: 'Environment / Sewage (Line 15)' },
  { key: 'purpose_housing',     label: 'Housing (Line 16)' },
  { key: 'purpose_utilities',   label: 'Utilities (Line 17)' },
  { key: 'purpose_other',       label: 'Other (Line 18)' },
];

const EMPTY = {
  issuer_name:'', issuer_ein:'', street_address:'', city_state_zip:'',
  contact_name_title:'', contact_phone:'', cusip:'', name_of_issue:'',
  purpose_education:'', purpose_health:'', purpose_transport:'',
  purpose_safety:'', purpose_environment:'', purpose_housing:'',
  purpose_utilities:'', purpose_other:'', purpose_other_desc:'',
  purpose_education_checked:false, purpose_health_checked:false,
  purpose_transport_checked:false, purpose_safety_checked:false,
  purpose_environment_checked:false, purpose_housing_checked:false,
  purpose_utilities_checked:false, purpose_other_checked:false,
  date_of_issue:'', final_maturity_date:'',
  issue_price:'', par_amount:'', wam:'', yield_rate:'',
  accrued_interest:'', credit_enhancement:'', reserve_fund:'',
  has_refunding_component:false,
  refund_taxexempt:'', refunded_wam_taxexempt:'',
  refund_taxable:'', refunded_wam_taxable:'',
  last_call_date:'', refunded_issue_dates:'',
  cb_bq:false, cb_remediation:false, cb_sec148_monitor:false,
  cb_reimbursement:false, reimbursement_amt:'', reimbursement_intent_date:'',
};

const Hdr = ({t}) => (
  <div style={{background:'var(--ribbon-bg)',color:'var(--ribbon-text)',padding:'6px 14px',
    fontSize:'12px',fontWeight:'600',letterSpacing:'0.05em',textTransform:'uppercase',
    borderRadius:'4px',marginBottom:'12px',marginTop:'4px'}}>{t}</div>
);

const F = ({label,name,value,onChange,placeholder='',half=false,ro=false,warn=''}) => (
  <div style={{marginBottom:'12px',width:half?'calc(50% - 6px)':'100%'}}>
    <label style={{display:'block',fontSize:'11px',fontWeight:'600',
      color: warn ? '#7a5c00' : 'var(--text-secondary,#666)',marginBottom:'4px',
      textTransform:'uppercase',letterSpacing:'0.04em'}}>{label}{warn ? ' ⚠' : ''}</label>
    <input type="text" name={name} value={value||''} onChange={onChange}
      placeholder={placeholder} readOnly={ro}
      style={{width:'100%',padding:'7px 10px',
        border: warn ? '1px solid #f0c040' : '1px solid var(--border-color)',
        borderRadius:'4px',fontSize:'13px',boxSizing:'border-box',outline:'none',
        background: warn ? '#fffbea' : ro ? 'var(--bg-secondary)' : 'var(--bg-primary)',
        color:'var(--text-primary)'}} />
    {warn && <div style={{fontSize:'11px',color:'#7a5c00',marginTop:'3px',fontStyle:'italic'}}>{warn}</div>}
  </div>
);

const Chk = ({label,name,checked,onChange}) => (
  <label style={{display:'flex',alignItems:'flex-start',gap:'8px',marginBottom:'10px',
    cursor:'pointer',fontSize:'13px',color:'var(--text-primary)'}}>
    <input type="checkbox" name={name} checked={!!checked} onChange={onChange}
      style={{marginTop:'2px',accentColor:'var(--accent-color)'}} />
    <span>{label}</span>
  </label>
);

const Form8038G = () => {
  const [step, setStep] = useState('upload');
  const [nFile, setNFile] = useState(null);
  const [pFile, setPFile] = useState(null);
  const [fd, setFd] = useState({...EMPTY});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [warns, setWarns] = useState([]);
  const [confidence, setConfidence] = useState({});
  const [preparer, setPreparer] = useState({});
  const nRef = useRef(); const pRef = useRef();

  useEffect(() => {
    fetch('/api/8038g/preparer_defaults')
      .then(r => r.ok ? r.json() : {})
      .then(d => setPreparer(d))
      .catch(() => {});
  }, []);

  const ch = e => {
    const {name,value,type,checked} = e.target;
    setFd(p => ({...p, [name]: type==='checkbox' ? checked : value}));
  };

  const doExtract = async () => {
    if (!nFile) { setErr('Please select a numbers packet PDF.'); return; }
    setErr(''); setBusy(true);
    try {
      const form = new FormData();
      form.append('file', nFile);
      if (pFile) form.append('prior_8038g', pFile);
      const res = await fetch('/api/8038g/extract', {method:'POST', body:form});
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Extraction failed');
      const m = {...EMPTY};
      Object.entries(data).forEach(([k,v]) => {
        if (v !== null && v !== undefined && v !== '') m[k] = typeof v==='number' ? String(v) : v;
      });
      setFd(m); setWarns(data._warnings||[]); setConfidence(data._confidence||{}); setStep('review');
    } catch(e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const doGenerate = async () => {
    setErr(''); setBusy(true);
    try {
      const payload = {...fd};
      // Auto-fill single-purpose amount from issue_price
      const checkedPurposes = PURPOSE_OPTIONS.filter(p => payload[p.key+'_checked']);
      const issuePrice = parseFloat(String(payload.issue_price||'').replace(/,/g,'')) || null;
      if (checkedPurposes.length === 1) {
        payload[checkedPurposes[0].key] = issuePrice;
      }
      // Clear unchecked purposes
      PURPOSE_OPTIONS.forEach(p => { if (!payload[p.key+'_checked']) payload[p.key] = null; });
      const nums = ['issue_price','par_amount','wam','yield_rate','accrued_interest',
        'credit_enhancement','reserve_fund','refund_taxexempt','refunded_wam_taxexempt',
        'refund_taxable','refunded_wam_taxable','reimbursement_amt',
        ...PURPOSE_OPTIONS.map(p=>p.key)];
      nums.forEach(k => {
        const n = parseFloat(String(payload[k]||'').replace(/,/g,''));
        payload[k] = isNaN(n) ? null : n;
      });
      const res = await fetch('/api/8038g/generate', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify(payload)
      });
      if (!res.ok) { const d=await res.json().catch(()=>({})); throw new Error(d.detail||'Failed'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href=url;
      const iss = (fd.issuer_name||'issuer').replace(/[^a-zA-Z0-9]/g,'_').substring(0,30);
      const dt = (fd.date_of_issue||'').replace(/\//g,'');
      a.download = `8038G_${iss}_${dt}.pdf`; a.click();
      URL.revokeObjectURL(url); setStep('done');
    } catch(e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const reset = () => { setStep('upload'); setFd({...EMPTY}); setNFile(null); setPFile(null); setErr(''); setWarns([]); setConfidence({}); };

  const cs = {flex:1,overflowY:'auto',padding:'24px',background:'var(--bg-primary)',color:'var(--text-primary)',fontFamily:'DM Sans,sans-serif'};
  const card = {background:'var(--bg-secondary)',border:'1px solid var(--border-color)',borderRadius:'8px',padding:'20px',marginBottom:'20px'};
  const btn = (v='primary') => ({padding:'9px 20px',borderRadius:'5px',cursor:'pointer',fontSize:'13px',fontWeight:'600',
    border:v==='secondary'?'1px solid var(--border-color)':'none',
    background:v==='primary'?'var(--accent-color)':'var(--bg-secondary)',
    color:v==='secondary'?'var(--text-primary)':'#fff', opacity:busy?0.7:1});
  const errdiv = <div style={{color:'#c0392b',fontSize:'13px',marginBottom:'16px',padding:'10px',background:'#fdecea',borderRadius:'4px'}}>{err}</div>;
  const row = {display:'flex',gap:'12px'};

  if (step==='upload') return (
    <div style={cs}>
      <h2 style={{margin:'0 0 6px 0',fontSize:'20px',fontWeight:'700'}}>IRS Form 8038-G Generator</h2>
      <p style={{margin:'0 0 24px 0',fontSize:'13px',color:'var(--text-secondary,#666)'}}>
        Upload a numbers packet PDF to auto-populate the form. Optionally upload a prior 8038-G to pre-fill issuer information.
      </p>
      <div style={card}>
        <Hdr t="Step 1 — Numbers Packet (required)" />
        <div onClick={()=>nRef.current.click()} style={{border:`2px dashed ${nFile?'var(--accent-color)':'var(--border-color)'}`,borderRadius:'6px',padding:'28px',textAlign:'center',cursor:'pointer',background:nFile?'var(--accent-subtle)':'var(--bg-primary)'}}>
          <div style={{fontSize:'28px',marginBottom:'8px'}}>📄</div>
          {nFile ? <div style={{color:'var(--accent-color)',fontWeight:'600'}}>{nFile.name}</div>
                 : <div style={{color:'var(--text-secondary,#666)',fontSize:'13px'}}>Click to select numbers packet PDF</div>}
        </div>
        <input ref={nRef} type="file" accept=".pdf" style={{display:'none'}} onChange={e=>{setNFile(e.target.files[0]||null);e.target.value='';}} />
      </div>
      <div style={card}>
        <Hdr t="Step 2 — Prior 8038-G (optional — pre-fills issuer info)" />
        <div onClick={()=>pRef.current.click()} style={{border:`2px dashed ${pFile?'var(--accent-color)':'var(--border-color)'}`,borderRadius:'6px',padding:'22px',textAlign:'center',cursor:'pointer',background:pFile?'var(--accent-subtle)':'var(--bg-primary)'}}>
          <div style={{fontSize:'24px',marginBottom:'6px'}}>📋</div>
          {pFile ? <div style={{color:'var(--accent-color)',fontWeight:'600'}}>{pFile.name}</div>
                 : <div style={{color:'var(--text-secondary,#666)',fontSize:'13px'}}>Click to select a prior 8038-G PDF (optional)</div>}
        </div>
        <input ref={pRef} type="file" accept=".pdf" style={{display:'none'}} onChange={e=>{setPFile(e.target.files[0]||null);e.target.value='';}} />
      </div>
      {err && errdiv}
      <button style={btn()} onClick={doExtract} disabled={!nFile||busy}>{busy?'⏳ Extracting…':'🔍 Extract & Review'}</button>
    </div>
  );

  if (step==='done') return (
    <div style={{...cs,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'300px'}}>
      <div style={{fontSize:'48px',marginBottom:'16px'}}>✅</div>
      <h3 style={{margin:'0 0 8px 0'}}>Form 8038-G Generated</h3>
      <p style={{color:'var(--text-secondary,#666)',marginBottom:'24px',textAlign:'center'}}>Your completed form has been downloaded. Review it before filing.</p>
      <button style={btn()} onClick={reset}>Start New Form</button>
    </div>
  );

  return (
    <div style={cs}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px'}}>
        <div>
          <h2 style={{margin:'0 0 4px 0',fontSize:'20px',fontWeight:'700'}}>Review & Generate</h2>
          <p style={{margin:0,fontSize:'13px',color:'var(--text-secondary,#666)'}}>Review all extracted fields before generating the PDF.</p>
        </div>
        <button style={btn('secondary')} onClick={reset}>← Start Over</button>
      </div>
      {warns.length>0 && (
        <div style={{background:'#fffbea',border:'1px solid #f0c040',borderRadius:'5px',padding:'10px 14px',marginBottom:'16px',fontSize:'12px',color:'#7a5c00'}}>
          <strong>⚠ Extraction notes:</strong>
          <ul style={{margin:'4px 0 0 0',paddingLeft:'18px'}}>{warns.map((w,i)=><li key={i}>{w}</li>)}</ul>
        </div>
      )}
      <div style={card}>
        <Hdr t="Part I — Issuer Information" />
        <F label="Issuer Name (Line 1)" name="issuer_name" value={fd.issuer_name} onChange={ch} />
        <div style={row}>
          <F label="Issuer EIN (Line 2)" name="issuer_ein" value={fd.issuer_ein} onChange={ch} placeholder="XX-XXXXXXX" half />
          <F label="CUSIP (Line 9)" name="cusip" value={fd.cusip} onChange={ch} placeholder="9-character CUSIP" half />
        </div>
        <F label="Street Address (Line 4)" name="street_address" value={fd.street_address} onChange={ch} />
        <F label="City, State, ZIP (Line 6)" name="city_state_zip" value={fd.city_state_zip} onChange={ch} />
        <div style={row}>
          <F label="Contact Name & Title (Line 10a)" name="contact_name_title" value={fd.contact_name_title} onChange={ch} half />
          <F label="Contact Phone (Line 10b)" name="contact_phone" value={fd.contact_phone} onChange={ch} placeholder="(XXX) XXX-XXXX" half />
        </div>
        <F label="Name of Issue (Line 8)" name="name_of_issue" value={fd.name_of_issue} onChange={ch} />
      </div>
      <div style={card}>
        <Hdr t="Part II — Type of Issue" />
        <p style={{fontSize:'12px',color:'var(--text-secondary,#666)',marginTop:0,marginBottom:'14px'}}>
          Check the applicable purpose(s). If one box: full issue price fills automatically.
          If two boxes: enter the allocation for each.
        </p>
        {PURPOSE_OPTIONS.map(p => (
          <Chk key={p.key} label={p.label} name={p.key+'_checked'} checked={!!fd[p.key+'_checked']} onChange={ch} />
        ))}
        {(() => {
          const checked = PURPOSE_OPTIONS.filter(p => fd[p.key+'_checked']);
          const issuePrice = parseFloat(String(fd.issue_price||'').replace(/,/g,'')) || 0;
          if (checked.length === 0) return null;
          if (checked.length === 1) {
            const p = checked[0];
            return (
              <div style={{paddingLeft:'24px',marginTop:'6px'}}>
                <div style={{fontSize:'12px',color:'var(--text-secondary,#666)',marginBottom:'8px'}}>
                  Full issue price ({issuePrice ? issuePrice.toLocaleString('en-US',{minimumFractionDigits:2}) : '—'}) will be entered on {p.label}.
                </div>
                {p.key === 'purpose_other' && (
                  <F label="Line 18 — Description of Projects" name="purpose_other_desc" value={fd.purpose_other_desc} onChange={ch} placeholder="Describe the projects" />
                )}
              </div>
            );
          }
          if (checked.length === 2) {
            return (
              <div style={{paddingLeft:'24px',marginTop:'6px'}}>
                <p style={{fontSize:'12px',color:'var(--text-secondary,#666)',marginTop:0,marginBottom:'10px'}}>
                  Enter the issue price allocable to each purpose (must total {issuePrice ? issuePrice.toLocaleString('en-US',{minimumFractionDigits:2}) : 'issue price'}).
                </p>
                {checked.map(p => (
                  <F key={p.key} label={p.label} name={p.key} value={fd[p.key]} onChange={ch} placeholder="0.00" half />
                ))}
                {checked.some(p => p.key === 'purpose_other') && (
                  <F label="Line 18 — Description of Projects" name="purpose_other_desc" value={fd.purpose_other_desc} onChange={ch} placeholder="Describe the projects" />
                )}
              </div>
            );
          }
          return <div style={{color:'#c0392b',fontSize:'12px',paddingLeft:'24px'}}>Maximum 2 purposes allowed.</div>;
        })()}
      </div>
      <div style={card}>
        <Hdr t="Part III — Description of Bonds (Line 21)" />
        <div style={row}>
          <F label="Date of Issue (Line 7)" name="date_of_issue" value={fd.date_of_issue} onChange={ch} placeholder="MM/DD/YYYY" half />
          <F label="Final Maturity Date (21a)" name="final_maturity_date" value={fd.final_maturity_date} onChange={ch} placeholder="MM/DD/YYYY" half />
        </div>
        <div style={row}>
          <F label="Issue Price (21b)" name="issue_price" value={fd.issue_price} warn={confidence["issue_price"]||""} onChange={ch} placeholder="0.00" half />
          <F label="Par Amount (21c)" name="par_amount" value={fd.par_amount} warn={confidence["par_amount"]||""} onChange={ch} placeholder="0.00" half />
        </div>
        <div style={row}>
          <F label="Weighted Avg Maturity — years (21d)" name="wam" value={fd.wam} warn={confidence["wam"]||""} onChange={ch} placeholder="0.0000" half />
          <F label="Yield % — arbitrage yield (21e)" name="yield_rate" value={fd.yield_rate} warn={confidence["yield_rate"]||""} onChange={ch} placeholder="0.0000" half />
        </div>
      </div>
      <div style={card}>
        <Hdr t="Part IV — Uses of Proceeds (additional items)" />
        <p style={{fontSize:'12px',color:'var(--text-secondary,#666)',marginTop:0,marginBottom:'14px'}}>Issuance costs and refunding amounts are auto-calculated. Enter additional items below.</p>
        <div style={row}>
          <F label="Accrued Interest (Line 22)" name="accrued_interest" value={fd.accrued_interest} onChange={ch} placeholder="0.00" half />
          <F label="Credit Enhancement (Line 25)" name="credit_enhancement" value={fd.credit_enhancement} warn={confidence["credit_enhancement"]||""} onChange={ch} placeholder="0.00" half />
        </div>
        <F label="Reserve Fund (Line 26)" name="reserve_fund" value={fd.reserve_fund} warn={confidence["reserve_fund"]||""} onChange={ch} placeholder="0.00" half />
      </div>
      <div style={card}>
        <Hdr t="Part V — Refunded Bonds" />
        <Chk label="This issue has a refunding component" name="has_refunding_component" checked={fd.has_refunding_component} onChange={ch} />
        {fd.has_refunding_component && (<>
          <div style={row}>
            <F label="Refunded Tax-Exempt Amount (Line 27)" name="refund_taxexempt" value={fd.refund_taxexempt} warn={confidence["refund_taxexempt"]||""} onChange={ch} placeholder="0.00" half />
            <F label="Remaining WAM of Refunded TE Bonds — years (Line 31)" name="refunded_wam_taxexempt" value={fd.refunded_wam_taxexempt} warn={confidence["refunded_wam_taxexempt"]||""} onChange={ch} placeholder="0.0000" half />
          </div>
          <div style={row}>
            <F label="Refunded Taxable Amount (Line 28)" name="refund_taxable" value={fd.refund_taxable} warn={confidence["refund_taxable"]||""} onChange={ch} placeholder="0.00" half />
            <F label="Remaining WAM of Refunded Taxable — years (Line 32)" name="refunded_wam_taxable" value={fd.refunded_wam_taxable} warn={confidence["refunded_wam_taxable"]||""} onChange={ch} placeholder="0.0000" half />
          </div>
          <div style={row}>
            <F label="Last Call Date of Refunded TE Bonds (Line 33)" name="last_call_date" value={fd.last_call_date} warn={confidence["last_call_date"]||""} onChange={ch} placeholder="MM/DD/YYYY" half />
            <F label="Date(s) Refunded Bonds Issued (Line 34)" name="refunded_issue_dates" value={fd.refunded_issue_dates} warn={confidence["refunded_issue_dates"]||""} onChange={ch} placeholder="MM/DD/YYYY" half />
          </div>
        </>)}
      </div>
      <div style={card}>
        <Hdr t="Part VI — Miscellaneous" />
        <Chk label="Line 39 — Bank qualified (§265(b)(3) small issuer exception)" name="cb_bq" checked={fd.cb_bq} onChange={ch} />
        <Chk label="Line 43 — Written procedures for nonqualified bond remediation" name="cb_remediation" checked={fd.cb_remediation} onChange={ch} />
        <Chk label="Line 44 — Written procedures to monitor §148 requirements" name="cb_sec148_monitor" checked={fd.cb_sec148_monitor} onChange={ch} />
        <Chk label="Line 45a — Proceeds used to reimburse prior expenditures" name="cb_reimbursement" checked={fd.cb_reimbursement} onChange={ch} />
        {fd.cb_reimbursement && (
          <div style={{paddingLeft:'24px'}}>
            <div style={row}>
              <F label="Reimbursement Amount" name="reimbursement_amt" value={fd.reimbursement_amt} onChange={ch} placeholder="0.00" half />
              <F label="Official Intent Date (Line 45b)" name="reimbursement_intent_date" value={fd.reimbursement_intent_date} onChange={ch} placeholder="MM/DD/YYYY" half />
            </div>
          </div>
        )}
      </div>
      <div style={card}>
        <Hdr t="Paid Preparer (pre-filled)" />
        <div style={{display:'flex',gap:'12px',flexWrap:'wrap'}}>
          <F label="Preparer Name" name="_p1" value={preparer.preparer_name||''} onChange={()=>{}} ro half />
          <F label="PTIN" name="_p2" value={preparer.preparer_ptin||''} onChange={()=>{}} ro half />
          <F label="Firm" name="_p3" value={preparer.preparer_firm||''} onChange={()=>{}} ro half />
          <F label="Firm EIN" name="_p4" value={preparer.preparer_ein||''} onChange={()=>{}} ro half />
          <F label="Address" name="_p5" value={preparer.preparer_address||''} onChange={()=>{}} ro />
          <F label="Phone" name="_p6" value={preparer.preparer_phone||''} onChange={()=>{}} ro half />
        </div>
      </div>
      {err && errdiv}
      <div style={{display:'flex',gap:'12px',paddingBottom:'32px'}}>
        <button style={btn()} onClick={doGenerate} disabled={busy}>{busy?'⏳ Generating PDF…':'📄 Generate 8038-G PDF'}</button>
        <button style={btn('secondary')} onClick={reset}>Start Over</button>
      </div>
    </div>
  );
};

export default Form8038G;
